import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CompanyLogo from './components/CompanyLogo';
import LoginForm from './components/LoginForm';
import LoginCard from './components/LoginCard';
import LoginBackground from './components/LoginBackground';

const LoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading, error: authError, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLogoutMessage, setShowLogoutMessage] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    // If user is already authenticated, redirect them to their appropriate dashboard
    if (user) {
      // Get role from localStorage (which has the correct role from database)
      const currentUserStr = localStorage.getItem('currentUser');
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
      const userRole = currentUser?.role || user.user_metadata?.role || 'viewer';
      
      // Redirect all users to administrative dashboard
      navigate('/administrative-dashboard');
    }
  }, [user, navigate]);

  // Check if user was redirected due to logout
  useEffect(() => {
    const currentUser = localStorage.getItem('currentUser');
    if (!currentUser && location.state?.from) {
      // User was redirected from another page (likely due to logout)
      setShowLogoutMessage(true);
    }
  }, [location.state]);

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError('');
    setShowLogoutMessage(false); // Clear logout message when user starts logging in

    try {
      const result = await signIn(formData.email, formData.password);
      
      if (result.data?.user) {
        // Wait a moment for the AuthContext to fetch the role from database
        // The AuthContext will set the localStorage with the correct role
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Get the role from localStorage (which should now have the correct role from database)
        const currentUserStr = localStorage.getItem('currentUser');
        const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
        const userRole = currentUser?.role;

        // Redirect to intended destination or administrative dashboard
        const from = location.state?.from?.pathname;
        if (from) {
          navigate(from, { replace: true });
        } else {
          // Redirect all users to administrative dashboard
          navigate('/administrative-dashboard', { replace: true });
        }
      } else if (result.error) {
        setError(result.error.message || 'Login failed. Please try again.');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <LoginBackground />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Company Branding */}
        <CompanyLogo />

        {/* Logout Message */}
        {showLogoutMessage && (
          <div className="mb-4 p-3 bg-info/10 border border-info/20 rounded-md">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-info rounded-full"></div>
              <p className="text-sm text-info">
                Sie wurden automatisch abgemeldet. Bitte melden Sie sich erneut an.
              </p>
            </div>
          </div>
        )}

        {/* Login Card */}
        <LoginCard>
          <LoginForm 
            onLogin={handleLogin}
            isLoading={isLoading}
            error={error || authError?.message}
          />
        </LoginCard>
      </div>
    </div>
  );
};

export default LoginScreen;